import React, { useEffect, useRef } from "react";
import moment from "moment";
import { fmt } from "./poHelpers";
import { hasRole } from "../../util/utilFunction";

const jenisOf = (e) =>
  e && e.isMaju ? "maju" : e && e.isMundur ? "mundur" : "";

/**
 * Ringkas semua entri jadwal pada SATU tanggal menjadi satu sel.
 * Entri pada tanggal yang sama dijumlah BERSIH (entri negatif ikut mengurangi), karena
 * kirimPO memecah jumlah dengan cara mengurangi satu entri dan menambah entri baru.
 * Status TIDAK diambil dari entri pertama (bisa saja penanda jumlah 0 yang sudah isKirim):
 *   1. jumlah belum kirim > 0           -> kuning/pink/hitam, angka = yang belum kirim
 *   2. tidak ada belum kirim, ada batal -> merah
 *   3. jumlah sudah kirim > 0           -> hijau
 *   4. selain itu: jumlah 0 tanpa flag  -> biru (PO awal); lainnya kosong
 * `obj` dikirim ke handlePickTanggal, jadi popup edit membaca entri yang bisa diedit.
 */
const summarizeDate = (entries) => {
  const net = (arr) => arr.reduce((s, e) => s + Number(e.jumlah || 0), 0);
  const firstPositive = (arr) =>
    arr.find((e) => Number(e.jumlah) > 0) || arr[0];

  const pendE = entries.filter((e) => !e.isKirim && !e.isBatal);
  const batalE = entries.filter((e) => e.isBatal);
  const sentE = entries.filter((e) => e.isKirim && !e.isBatal);
  const pending = net(pendE);
  const batal = net(batalE);
  const sent = net(sentE);

  if (pending > 0) {
    const f = firstPositive(pendE);
    return {
      obj: { ...f, jumlah: pending },
      status: `${jenisOf(f)}belum`,
      sent: Math.max(sent, 0),
    };
  }
  if (batal > 0) {
    const f = firstPositive(batalE);
    return { obj: { ...f, jumlah: batal }, status: "batal", sent: 0 };
  }
  if (sent > 0) {
    const f = firstPositive(sentE);
    return {
      obj: { ...f, jumlah: sent },
      status: `${jenisOf(f)}sudah`,
      sent: 0,
    };
  }
  const awal = entries.some(
    (e) =>
      Number(e.jumlah) === 0 &&
      !e.isKirim &&
      !e.isBatal &&
      !e.isMaju &&
      !e.isMundur
  );
  return {
    obj: { ...entries[0], jumlah: 0 },
    status: awal ? "awal" : "",
    sent: 0,
  };
};

const mergeByDate = (jadwalKirim = []) => {
  const grouped = {};
  jadwalKirim.forEach((j) => {
    (grouped[j.date] = grouped[j.date] || []).push(j);
  });
  const map = {};
  Object.keys(grouped).forEach(
    (date) => (map[date] = summarizeDate(grouped[date]))
  );
  return map;
};

const sentOf = (d) =>
  d.jadwalKirim.reduce(
    (s, a) => (a.isKirim || a.isBatal ? s + Number(a.jumlah) : s),
    0
  );

export { summarizeDate };

export default function PoInputTable({ po }) {
  const {
    dataTemp,
    dateTempListPo,
    dataUser,
    dataBarangJadi,
    isEditPo,
    handleChangeEditPo,
    handlePickTanggal,
    bulanPo,
    tahunPo,
  } = po;

  const showStock = hasRole(dataUser, "ppic_admin", "super_user");

  // lebar kolom beku; offset "left" dihitung otomatis sehingga tidak perlu angka manual
  const cols = [
    { key: "no", label: "No.", w: 40 },
    { key: "customer", label: "Cust", w: 56 },
    { key: "noPo", label: "No. PO", w: 108 },
    { key: "noLine", label: "Line", w: 56 },
    { key: "noSkk", label: "SKK", w: 116 },
    { key: "kodeLine", label: "Kode Line", w: 92 },
    { key: "item", label: "Item", w: 280 },
    { key: "total", label: "Total", w: 76 },
    { key: "sisa", label: "Sisa", w: 76 },
    ...(showStock ? [{ key: "stock", label: "Stock", w: 76 }] : []),
  ];
  let acc = 0;
  cols.forEach((c) => {
    c.left = acc;
    acc += c.w;
  });
  const frozenWidth = acc;
  const cellStyle = (c) => ({
    left: c.left,
    width: c.w,
    minWidth: c.w,
    maxWidth: c.w,
  });

  const rows = dataTemp || [];
  const dates = [...(dateTempListPo || [])].sort(
    (a, b) => Number(a.tanggal) - Number(b.tanggal)
  );

  const now = moment().utcOffset("+0700");
  const isCurrentMonth =
    Number(bulanPo) === now.month() + 1 && Number(tahunPo) === now.year();
  const today = now.format("DD");

  const todayRef = useRef(null);
  useEffect(() => {
    if (todayRef.current) {
      todayRef.current.scrollIntoView({
        behavior: "smooth",
        inline: "start",
        block: "nearest",
      });
    }
  }, [rows.length, bulanPo, tahunPo]);

  const totalPo = rows.reduce((s, a) => s + a.jumlah, 0);
  const totalSisa = rows.reduce((s, a) => s + (a.jumlah - sentOf(a)), 0);

  const stockOf = (d) => {
    const company =
      dataBarangJadi && dataBarangJadi.find((i) => i.company === d.customer);
    const found = company && company.data.find((o) => o.kodeKirim === d.itemPo);
    return found && found.weight;
  };

  const editable = (d, key, width, upper) =>
    isEditPo ? (
      <input
        className="po-input po-input--cell"
        style={{ width }}
        type="text"
        name={key}
        value={d[key] || ""}
        onChange={(e) => handleChangeEditPo(e, key, d.id)}
      />
    ) : (
      d[key]
    );

  return (
    <div className="po-table-wrap">
      <table className="po-table po-grid">
        <thead>
          <tr>
            {cols.map((c) => (
              <th
                key={c.key}
                className="po-th po-sticky"
                style={{
                  ...cellStyle(c),
                  textAlign: c.key === "no" ? "center" : "left",
                }}
              >
                {c.label}
              </th>
            ))}
            {dates.map((ele) => {
              const isToday = isCurrentMonth && ele.tanggal === today;
              const isSunday = moment(ele.date).day() === 0;
              return (
                <th
                  key={ele.tanggal}
                  ref={isToday ? todayRef : null}
                  className={`po-th po-th--date ${
                    isToday ? "po-th--today" : isSunday ? "po-th--sunday" : ""
                  }`}
                  style={{ scrollMarginLeft: frozenWidth + 8 }}
                >
                  {ele.tanggal}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((d, index) => {
            const byDate = mergeByDate(d.jadwalKirim);
            const sisa = d.jumlah - sentOf(d);
            const cell = (key) => cols.find((c) => c.key === key);
            return (
              <tr
                key={d.id || index}
                className={`po-row ${index % 2 ? "po-row--alt" : ""}`}
              >
                <td
                  className="po-td po-td--num po-sticky"
                  style={cellStyle(cell("no"))}
                >
                  {index + 1}.
                </td>
                <td
                  className="po-td po-sticky"
                  style={cellStyle(cell("customer"))}
                >
                  {editable(d, "customer", 40)}
                </td>
                <td
                  className="po-td po-nowrap po-sticky"
                  style={cellStyle(cell("noPo"))}
                >
                  {editable(d, "noPo", 92)}
                </td>
                <td
                  className="po-td po-sticky"
                  style={cellStyle(cell("noLine"))}
                >
                  {editable(d, "noLine", 40)}
                </td>
                <td
                  className="po-td po-nowrap po-sticky"
                  style={cellStyle(cell("noSkk"))}
                >
                  {editable(d, "noSkk", 100)}
                </td>
                <td
                  className="po-td po-nowrap po-sticky"
                  style={cellStyle(cell("kodeLine"))}
                >
                  {d.kodeLine}
                </td>
                <td
                  className="po-td po-compact po-sticky"
                  style={cellStyle(cell("item"))}
                >
                  {d.customer === "YBI" ? (
                    <>
                      {d.kodeBarang}
                      <br />
                    </>
                  ) : null}
                  {d.itemPo}
                </td>
                <td
                  className="po-td po-nowrap po-sticky"
                  style={cellStyle(cell("total"))}
                >
                  {fmt(d.jumlah)}
                </td>
                <td
                  className="po-td po-nowrap po-sticky"
                  style={cellStyle(cell("sisa"))}
                >
                  {fmt(sisa)}
                </td>
                {showStock ? (
                  <td
                    className="po-td po-nowrap po-sticky"
                    style={cellStyle(cell("stock"))}
                  >
                    {fmt(stockOf(d))}
                  </td>
                ) : null}

                {dates.map((ele) => {
                  const g = byDate[ele.date];
                  const st = g ? g.status : "";
                  return (
                    <td
                      key={ele.date}
                      className={`po-td po-td--date ${
                        st ? `po-st po-st--${st}` : ""
                      }`}
                      onClick={() => handlePickTanggal(d, ele, g && g.obj)}
                    >
                      {g && g.obj.jumlah ? fmt(g.obj.jumlah) : ""}
                      {g && g.sent > 0 && g.status.endsWith("belum") ? (
                        <div className="po-cell-sent">
                          &#10003; {fmt(g.sent)}
                        </div>
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            );
          })}
          <tr className="po-foot">
            <td className="po-td" colSpan={7} style={{ textAlign: "right" }}>
              TOTAL PO
            </td>
            <td className="po-td po-nowrap">{fmt(totalPo)}</td>
            <td className="po-td po-nowrap">{fmt(totalSisa)}</td>
            {showStock ? <td className="po-td" /> : null}
            <td className="po-td" colSpan={dates.length} />
          </tr>
        </tbody>
      </table>
    </div>
  );
}
